import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Navigate } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Modal } from "@/components/ui/Modal/Modal";
import { Tabs } from "@/components/ui/Tabs/Tabs";
import { AppShell } from "@/components/app/AppShell";
import { useAuth } from "@/lib/AuthContext";
import { buildCsv, downloadCsv } from "@/lib/csv";
import {
  useAdminAudit,
  useAdminBilling,
  useAdminBillingFlows,
  useAdminCost,
  useAdminOverview,
  useClearTenantComp,
  useSetTenantComp,
  useSetTenantPlan,
  type AdminAuditAction,
  type AdminAuditEvent,
  type AdminBilling,
  type AdminBillingFlows,
  type AdminCost,
  type AdminOverview,
  type AdminTenant,
  type BillingPaymentRow,
  type BillingRenewal,
  type BillingTenant,
  type CacheHealthPoint,
  type CostByTenant,
} from "@/lib/queries";
import { PLAN_IDS, type PlanId } from "@notamen/core/billing";
import {
  COMP_DEFAULT_PLAN,
  COMP_DURATION_UNITS,
  COMP_PLAN_IDS,
  COMP_TYPES,
  compStatus,
  type CompDurationUnit,
  type CompPlanId,
  type CompType,
} from "@notamen/core/workspace";
import styles from "./AdminConsole.module.css";

/**
 * Humanize a positive millisecond span as a coarse "ends in" string for the
 * comp countdown: days when ≥1 day, else hours, else minutes, else "<1m".
 * Used by the tenant-row badge AND mirrors the wording the SPA banner uses.
 */
function humanizeRemaining(ms: number): string {
  if (ms <= 0) return "0m";
  const totalMin = Math.floor(ms / 60_000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin % (60 * 24)) / 60);
  const mins = totalMin % 60;
  if (days >= 1) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours >= 1) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  if (mins >= 1) return `${mins}m`;
  return "<1m";
}

/** Console-level query result, threaded into the two wired tabs so the one
 *  cached query feeds both Overview and Tenants (never fetched twice). */
type AdminData = ReturnType<typeof useAdminOverview>;

// ─── CSV export ───────────────────────────────────────────────────────
//
// "Export what you see": the header Export button downloads the ACTIVE data
// tab's primary dataset. Rather than lifting every tab's query into the body
// (which would fire the Cost/Audit/Billing fetches even when their tab is
// never opened), each data tab REGISTERS a lazy exporter via context when its
// data is ready; the header button calls it and is disabled (with a truthful
// title) on tabs that have nothing to export yet. Pure client-side — no
// backend endpoint.

/** A registered, lazy CSV export for the active tab. `build` runs only on click. */
interface TabExport {
  filename: string;
  build: () => string;
}

const AdminExportCtx = createContext<(exp: TabExport | null) => void>(() => {});

/** Register (and clear on unmount / dep-change) the active tab's exporter. */
function useRegisterExport(exp: TabExport | null, deps: unknown[]): void {
  const register = useContext(AdminExportCtx);
  useEffect(() => {
    register(exp);
    return () => register(null);
    // The exporter is rebuilt from `deps` (the tab's loaded data); `exp`/
    // `register` are derived from them and intentionally not in the array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** UTC date stamp for export filenames, e.g. 2026-06-04. */
function csvDateStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

function overviewToCsv(o: AdminOverview): string {
  return buildCsv(
    ["Metric", "Value"],
    [
      ["Workspaces", o.workspaces],
      ["Pending approval", o.pendingApproval],
      ["Users", o.users],
      ["Brands", o.brands],
      ["Drafts", o.drafts],
    ],
  );
}

function tenantsToCsv(rows: AdminTenant[]): string {
  return buildCsv(
    [
      "Workspace ID",
      "Name",
      "Plan",
      "Status",
      "Created",
      "Brands",
      "Members",
      "Comp type",
      "Comp plan",
      "Comp expires",
    ],
    rows.map((t) => [
      t.id,
      t.name,
      t.plan,
      t.status,
      t.createdAt,
      t.brandCount,
      t.memberCount,
      t.comp?.type ?? "",
      t.comp?.plan ?? "",
      t.comp?.expiresAt ?? "",
    ]),
  );
}

function costToCsv(rows: CostByTenant[]): string {
  return buildCsv(
    ["Tenant", "Workspace ID", "AI spend USD (30d)"],
    rows.map((r) => [r.name, r.workspaceId, r.spendUsd.toFixed(2)]),
  );
}

function auditToCsv(events: AdminAuditEvent[]): string {
  return buildCsv(
    ["Time", "Action", "Actor", "Target workspace", "Target user", "Summary"],
    events.map((e) => [
      e.createdAt,
      e.action,
      e.actorEmail || e.actorSub,
      e.targetWorkspaceId ?? "",
      e.targetUsername ?? "",
      e.summary,
    ]),
  );
}

function billingToCsv(rows: BillingTenant[]): string {
  return buildCsv(
    ["Tenant", "Workspace ID", "Plan", "Cadence", "MRR USD"],
    rows.map((r) => [r.name, r.workspaceId, r.plan, r.cadence, r.mrrUsd.toFixed(2)]),
  );
}

/**
 * AdminConsole — Phoenix Apps' cross-tenant operator console.
 *
 * STATUS: partially wired. Four of the seven sub-views read real
 * cross-tenant data; three are still scaffolds being built out one PR at a
 * time. There is intentionally no mock/sample data anywhere — a scaffold tab
 * shows an honest "not live yet" notice + empty slots, never fake numbers.
 *
 *   WIRED (real backend):
 *     Overview / Tenants  → GET /api/admin/overview (useAdminOverview)
 *     Cost                → GET /api/admin/cost     (useAdminCost)
 *     Audit               → GET /api/admin/audit    (useAdminAudit)
 *     Billing             → GET /api/admin/billing (useAdminBilling) for the
 *                           instant scan-only MRR / ARR / tenants-by-MRR, plus
 *                           GET /api/admin/billing/flows (useAdminBillingFlows)
 *                           for the live-Stripe sections: new + churned subs
 *                           this month, upcoming renewals, recent payments.
 *                           The two are SEPARATE so a slow/down Stripe never
 *                           breaks the instant MRR tile.
 *     + tenant plan / demo-trial comp mutations on each Tenants row
 *     + Export CSV — header button exports the active data tab's primary
 *       dataset (Overview/Tenants/Cost/Audit/Billing); Tenants panel has its
 *       own Export. Pure client-side download, no backend endpoint.
 *   SCAFFOLD (no backend yet — see SCAFFOLD_TABS below):
 *     Ops      → needs an alerting pipeline
 *     Support  → needs a support-inbox + intake form
 *   Disabled actions still pending their own PRs: Impersonate, Forward to #ops.
 *
 * This sits ABOVE the per-workspace context: it's the operator's view of
 * EVERY workspace, so it is rendered with no current workspace id and
 * reads admin/cross-tenant aggregates, not workspace-scoped rows.
 *
 * To wire a remaining scaffold tab: add the cross-tenant read in
 * packages/core / infra/lambdas/api (operator-only, gated on the caller's
 * sm-admins group, a bounded scan/Query — never a per-tenant Keys.* fan-out),
 * add the admin endpoint (403 to non-admins) + a TanStack Query hook in
 * apps/web/src/lib/queries, replace the tab's scaffold body, and drop it from
 * SCAFFOLD_TABS so its "not live yet" notice retires.
 *
 * Design source of truth: docs/design-mockups/admin.jsx + DESIGN_SPEC
 * §4.23–4.29.
 */

type TabKey =
  | "overview"
  | "tenants"
  | "billing"
  | "cost"
  | "ops"
  | "support"
  | "audit";

const TAB_ITEMS: { key: TabKey; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "tenants", label: "Tenants" },
  { key: "billing", label: "Billing" },
  { key: "cost", label: "Cost" },
  { key: "ops", label: "Ops" },
  { key: "support", label: "Support" },
  { key: "audit", label: "Audit" },
];

/**
 * Tabs whose cross-tenant backend isn't built yet — only these still show the
 * tab-level scaffold notice. Overview, Tenants, Cost, Audit, and Billing have
 * real backends, so the notice no longer renders over them (it used to render
 * globally, falsely covering the live tabs). Billing is PARTIALLY wired — its
 * still-pending pieces (renewals/payments panels + net-new/net-churn tiles)
 * carry their own per-element "not live yet" markers instead of a tab banner.
 */
const SCAFFOLD_TABS = new Set<TabKey>(["ops", "support"]);

export function AdminConsole() {
  const auth = useAuth();

  if (auth.status === "anonymous") {
    return <Navigate to="/signin?next=/admin" replace />;
  }

  // Operator gate. Mirrors AdminWaitlist: client-side gate here, and the
  // /api/admin/* endpoints will independently return 403 to non-admins.
  if (auth.status === "authenticated" && !auth.user?.isAdmin) {
    return (
      <AppShell currentWorkspaceId={undefined}>
        <div className={styles.page}>
          <div className={styles.denied}>
            <Icon name="lock" size={26} />
            <h1 className={styles.deniedTitle}>Operator access only</h1>
            <p className={styles.deniedBody}>
              The admin console is for Phoenix Apps operators. If you should
              have access, ask another admin to add you to the{" "}
              <code>sm-admins</code> Cognito group.
            </p>
            <Btn href="/account" variant="secondary">
              Back to your account
            </Btn>
          </div>
        </div>
      </AppShell>
    );
  }

  // Past the gate ⇒ operator (or still resolving). The cross-tenant query
  // lives in the body so it only mounts once we're past the non-admin gate
  // and never fires for a non-operator session.
  return <AdminConsoleBody />;
}

/**
 * Operator-only console body. Owns the single cross-tenant `useAdminOverview`
 * query and threads it into the Overview + Tenants tabs so both share one
 * cached fetch (the other five tabs stay scaffold).
 */
function AdminConsoleBody() {
  const [tab, setTab] = useState<TabKey>("overview");
  const admin = useAdminOverview();
  // The active data tab registers its exporter here; the header Export button
  // reads it. Stable identity so the registering effect doesn't re-fire.
  const [exporter, setExporter] = useState<TabExport | null>(null);
  const register = useCallback((exp: TabExport | null) => setExporter(exp), []);

  return (
    <AppShell currentWorkspaceId={undefined}>
      <AdminExportCtx.Provider value={register}>
      <div className={styles.page}>
        <header className={styles.head}>
          <div className={styles.headText}>
            <span className={styles.kicker}>Phoenix admin</span>
            <h1 className={styles.title}>
              <Icon name="lock" size={20} /> Admin console
            </h1>
            <p className={styles.subtitle}>
              Cross-tenant operator view — every workspace on Notamen.
            </p>
          </div>
          <div className={styles.headActions}>
            {/* SCAFFOLD: POST /api/admin/impersonate — operator assumes a
                tenant session (audited). Unwired. */}
            <Btn
              variant="secondary"
              disabled
              title="Not wired up yet"
              iconLeft={<Icon name="user" size={14} />}
            >
              Impersonate
            </Btn>
            {/* Exports the ACTIVE data tab's primary dataset (registered via
                AdminExportCtx). Disabled on tabs with nothing to export. */}
            <Btn
              variant="secondary"
              disabled={!exporter}
              title={
                exporter ? `Download ${exporter.filename}` : "Nothing to export on this tab"
              }
              iconLeft={<Icon name="arrow-up-right" size={14} />}
              onClick={() => {
                if (exporter) downloadCsv(exporter.filename, exporter.build());
              }}
            >
              Export CSV
            </Btn>
          </div>
        </header>

        {SCAFFOLD_TABS.has(tab) && <ScaffoldNotice />}

        <div className={styles.tabsBar}>
          <Tabs items={TAB_ITEMS} value={tab} onChange={(k) => setTab(k as TabKey)} />
        </div>

        {tab === "overview" && <OverviewTab admin={admin} />}
        {tab === "tenants" && <TenantsTab admin={admin} />}
        {tab === "billing" && <BillingTab />}
        {tab === "cost" && <CostTab />}
        {tab === "ops" && <OpsTab />}
        {tab === "support" && <SupportTab />}
        {tab === "audit" && <AuditTab />}
      </div>
      </AdminExportCtx.Provider>
    </AppShell>
  );
}

/** Honest, prominent label shown only on tabs whose backend isn't built yet. */
function ScaffoldNotice() {
  return (
    <div className={styles.scaffold} role="note">
      <Icon name="info" size={15} />
      <div>
        <strong>This tab isn’t live yet.</strong> Its cross-tenant backend
        isn’t built, so nothing here reads or writes real data and no operator
        actions run. The layout is in place to wire up next. Build status lives
        in <code>docs/FEATURE_CATALOG.md</code> (“Admin operator console”).
      </div>
    </div>
  );
}

/** Generic empty section header used by every sub-view. */
function SectionHead({ title, desc }: { title: string; desc: string }) {
  return (
    <div className={styles.sectionHead}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      <p className={styles.sectionDesc}>{desc}</p>
    </div>
  );
}

/** A labeled KPI slot with a "—" placeholder (no fake numbers). */
function StatRow({ labels }: { labels: { label: string; hint: string }[] }) {
  return (
    <div className={styles.statRow}>
      {labels.map((s) => (
        <div key={s.label} className={styles.stat}>
          <span className={styles.statValue}>—</span>
          <span className={styles.statLabel}>{s.label}</span>
          <span className={styles.statHint}>{s.hint}</span>
        </div>
      ))}
    </div>
  );
}

/** A bordered empty pane describing what will fill it (no mock rows). */
function EmptyPane({ icon, title, hint }: { icon: Parameters<typeof Icon>[0]["name"]; title: string; hint: string }) {
  return (
    <div className={styles.emptyPane}>
      <Icon name={icon} size={22} />
      <p>{title}</p>
      <p className={styles.emptyHint}>{hint}</p>
    </div>
  );
}

/** Quiet loading state for the wired Overview/Tenants tabs. */
function LoadingPane({ label }: { label: string }) {
  return (
    <div className={styles.emptyPane}>
      <Icon name="refresh" size={22} />
      <p>{label}</p>
    </div>
  );
}

/** Error state with the API message + a retry that refetches the shared query. */
function ErrorPane({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className={styles.emptyPane}>
      <Icon name="alert" size={22} />
      <p>Couldn’t load admin data.</p>
      <p className={styles.emptyHint}>{message}</p>
      {onRetry && (
        <Btn
          variant="secondary"
          size="sm"
          onClick={onRetry}
          iconLeft={<Icon name="refresh" size={13} />}
        >
          Retry
        </Btn>
      )}
    </div>
  );
}

/** Tenant status as a pill — only two states exist on the admin list. */
function TenantStatusPill({ status }: { status: AdminTenant["status"] }) {
  const active = status === "active";
  return (
    <span className={`${styles.tenantPill} ${active ? styles.pillActive : styles.pillPending}`}>
      <span className={styles.pillDot} aria-hidden="true" />
      {active ? "Active" : "Pending"}
    </span>
  );
}

/**
 * Plan dropdown on each tenant row — admin-only writer for
 * `Workspace.plan`. The select fires the mutation directly on change;
 * disabled while pending so a fast double-change can't race itself.
 * A small error message renders inline if the request 4xx/5xxs (most
 * likely cause: an old session whose Cognito group membership lapsed).
 */
function TenantPlanSelect({ tenant }: { tenant: AdminTenant }) {
  const mutation = useSetTenantPlan();
  const [pendingPlan, setPendingPlan] = useState<PlanId | null>(null);
  const [error, setError] = useState<string | null>(null);

  function onChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const next = e.target.value as PlanId;
    if (next === tenant.plan) return;
    setError(null);
    setPendingPlan(next);
    mutation.mutate(
      { workspaceId: tenant.id, plan: next },
      {
        onSuccess: () => setPendingPlan(null),
        onError: (err) => {
          setPendingPlan(null);
          setError(err.message);
        },
      },
    );
  }

  const displayed = pendingPlan ?? tenant.plan;
  return (
    <div className={styles.tenantPlanWrap}>
      <select
        className={styles.tenantPlanSelect}
        value={displayed}
        disabled={mutation.isPending}
        onChange={onChange}
        aria-label={`Plan for ${tenant.name || tenant.id}`}
      >
        {PLAN_IDS.map((p) => (
          <option key={p} value={p}>
            {/* Capitalize in JS — `text-transform: capitalize` on the
                select doesn't reliably reach <option> labels across
                browsers (Chrome/Safari render OS-native dropdowns). */}
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </option>
        ))}
      </select>
      {error && (
        <span className={styles.tenantPlanError} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

/** Short date for the "Created" column. Falls back to the raw value. */
function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// ─── Overview ─────────────────────────────────────────────────────────

function OverviewTab({ admin }: { admin: AdminData }) {
  const data = admin.data;
  useRegisterExport(
    data
      ? { filename: `admin-overview-${csvDateStamp()}.csv`, build: () => overviewToCsv(data.overview) }
      : null,
    [data],
  );
  return (
    <div className={styles.tabBody}>
      <SectionHead
        title="Platform overview"
        desc="Headline counts across every tenant on Notamen — workspaces, users, brands, and drafts generated platform-wide."
      />
      {/* Real cross-tenant counts from GET /api/admin/overview (useAdminOverview). */}
      <OverviewStats admin={admin} />
    </div>
  );
}

function OverviewStats({ admin }: { admin: AdminData }) {
  if (admin.isPending) {
    return <LoadingPane label="Loading platform counts…" />;
  }
  if (admin.isError) {
    return <ErrorPane message={admin.error.message} onRetry={() => void admin.refetch()} />;
  }

  const o: AdminOverview = admin.data.overview;
  const workspaceHint =
    o.pendingApproval > 0
      ? `${o.pendingApproval} pending approval`
      : "all approved";

  return (
    <div className={styles.statRow}>
      <Stat value={o.workspaces} label="Workspaces" hint={workspaceHint} />
      <Stat value={o.users} label="Users" hint="across all workspaces" />
      <Stat value={o.brands} label="Brands" hint="platform-wide" />
      <Stat value={o.drafts} label="Drafts" hint="generated, all tenants" />
    </div>
  );
}

/** A KPI tile showing a real count (mirrors the scaffold StatRow's shape). */
function Stat({ value, label, hint }: { value: number; label: string; hint: string }) {
  return (
    <div className={styles.stat}>
      <span className={`${styles.statValue} ${styles.statValueLive}`}>
        {value.toLocaleString()}
      </span>
      <span className={styles.statLabel}>{label}</span>
      <span className={styles.statHint}>{hint}</span>
    </div>
  );
}

// ─── Tenants ──────────────────────────────────────────────────────────

function TenantsTab({ admin }: { admin: AdminData }) {
  const tenants = admin.data?.tenants ?? [];
  const hasTenants = tenants.length > 0;
  const tenantsFile = `admin-tenants-${csvDateStamp()}.csv`;
  useRegisterExport(
    hasTenants ? { filename: tenantsFile, build: () => tenantsToCsv(tenants) } : null,
    [admin.data],
  );
  return (
    <div className={styles.tabBody}>
      <SectionHead
        title="Tenants"
        desc="Every workspace on Notamen, newest first — plan, status, brand and member counts, and when it was created."
      />
      {/* Real cross-tenant workspace list from GET /api/admin/overview
          (useAdminOverview, shared with the Overview tab). Operator-only
          aggregate across every WS#, not a workspace-scoped Keys.* query. */}
      <div className={styles.panel}>
        <div className={styles.panelHead}>
          <span>All tenants</span>
          {/* Client-side CSV of the tenant list shown below (no backend). */}
          <Btn
            variant="subtle"
            size="sm"
            disabled={!hasTenants}
            title={hasTenants ? "Download tenants CSV" : "No tenants to export"}
            onClick={() => downloadCsv(tenantsFile, tenantsToCsv(tenants))}
          >
            Export
          </Btn>
        </div>
        <TenantsTable admin={admin} />
      </div>
    </div>
  );
}

function TenantsTable({ admin }: { admin: AdminData }) {
  if (admin.isPending) {
    return <LoadingPane label="Loading tenants…" />;
  }
  if (admin.isError) {
    return <ErrorPane message={admin.error.message} onRetry={() => void admin.refetch()} />;
  }

  const tenants = admin.data.tenants;
  if (tenants.length === 0) {
    return (
      <EmptyPane
        icon="user"
        title="No tenants yet."
        hint="Workspaces appear here as soon as the first one is created."
      />
    );
  }

  return (
    <>
      <div className={styles.tenantHead}>
        <span>Workspace</span>
        <span>Plan</span>
        <span>Status</span>
        <span className={styles.alignRight}>Brands</span>
        <span className={styles.alignRight}>Members</span>
        <span className={styles.alignRight}>Created</span>
      </div>
      <ul className={styles.tenantList}>
        {tenants.map((t) => (
          <TenantRow key={t.id} tenant={t} />
        ))}
      </ul>
    </>
  );
}

function TenantRow({ tenant }: { tenant: AdminTenant }) {
  const name = tenant.name?.trim();
  return (
    <li className={styles.tenantRow}>
      <div className={styles.tenantGrid}>
        <div className={styles.tenantCell} data-col="Workspace">
          {name ? (
            <span className={styles.tenantName}>{name}</span>
          ) : (
            <span className={styles.tenantNameMuted}>{tenant.id}</span>
          )}
        </div>
        <div className={styles.tenantCell} data-col="Plan">
          <TenantPlanSelect tenant={tenant} />
        </div>
        <div className={styles.tenantCell} data-col="Status">
          <TenantStatusPill status={tenant.status} />
        </div>
        <div className={`${styles.tenantCell} ${styles.alignRight}`} data-col="Brands">
          {tenant.brandCount.toLocaleString()}
        </div>
        <div className={`${styles.tenantCell} ${styles.alignRight}`} data-col="Members">
          {tenant.memberCount.toLocaleString()}
        </div>
        <div className={`${styles.tenantCell} ${styles.alignRight}`} data-col="Created">
          {fmtDate(tenant.createdAt)}
        </div>
      </div>
      <TenantCompBar tenant={tenant} />
    </li>
  );
}

/**
 * Comp (demo/trial) controls for a tenant row: a status badge + live
 * countdown when a grant is present (EXPIRED styling once locked), a Revoke
 * action when present, and a "Grant demo/trial" button that opens the grant
 * modal. Renders below the main grid row so it reflows cleanly on phones.
 */
function TenantCompBar({ tenant }: { tenant: AdminTenant }) {
  // Live "ends in" countdown: re-derive once a minute so the badge stays
  // current without a refetch (the grant's expiry is a pure clock read).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!tenant.comp) return;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [tenant.comp]);

  const [grantOpen, setGrantOpen] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const clear = useClearTenantComp();

  const cs = compStatus(tenant.comp, now);

  function onRevoke() {
    setRevokeError(null);
    clear.mutate(
      { workspaceId: tenant.id },
      {
        onSuccess: () => setConfirmRevoke(false),
        onError: (err) => setRevokeError(err.message),
      },
    );
  }

  return (
    <div className={styles.compBar}>
      {tenant.comp ? (
        <>
          <span
            className={`${styles.compBadge} ${cs.locked ? styles.compBadgeExpired : styles.compBadgeActive}`}
          >
            <span className={styles.compBadgeType}>{tenant.comp.type.toUpperCase()}</span>
            <span className={styles.compBadgeDot} aria-hidden="true">
              ·
            </span>
            <span className={styles.compBadgePlan}>{tenant.comp.plan}</span>
            <span className={styles.compBadgeSep} aria-hidden="true" />
            <span className={styles.compBadgeTime}>
              {cs.locked ? "EXPIRED" : `ends in ${humanizeRemaining(cs.remainingMs)}`}
            </span>
          </span>
          {confirmRevoke ? (
            <span className={styles.compConfirm}>
              <span className={styles.compConfirmText}>
                Revoke and restore the previous plan?
              </span>
              <Btn
                variant="destructive"
                size="sm"
                onClick={onRevoke}
                loading={clear.isPending}
              >
                Revoke
              </Btn>
              <Btn
                variant="subtle"
                size="sm"
                onClick={() => setConfirmRevoke(false)}
                disabled={clear.isPending}
              >
                Keep
              </Btn>
            </span>
          ) : (
            <Btn variant="subtle" size="sm" onClick={() => setConfirmRevoke(true)}>
              Revoke
            </Btn>
          )}
          {revokeError && (
            <span className={styles.compError} role="alert">
              {revokeError}
            </span>
          )}
        </>
      ) : (
        <Btn
          variant="secondary"
          size="sm"
          onClick={() => setGrantOpen(true)}
          iconLeft={<Icon name="zap" size={13} />}
        >
          Grant demo/trial
        </Btn>
      )}
      <GrantCompModal
        tenant={tenant}
        open={grantOpen}
        onClose={() => setGrantOpen(false)}
      />
    </div>
  );
}

/**
 * Modal form to grant a complimentary demo/trial to a tenant. Type select
 * (demo|trial) drives the default plan (COMP_DEFAULT_PLAN), which the operator
 * can override — plan options are COMP_PLAN_IDS only (never enterprise).
 * Duration is a positive number + a unit (hours|days). Submit fires
 * useSetTenantComp; server validation errors surface inline.
 */
function GrantCompModal({
  tenant,
  open,
  onClose,
}: {
  tenant: AdminTenant;
  open: boolean;
  onClose: () => void;
}) {
  const mutation = useSetTenantComp();
  const [type, setType] = useState<CompType>("trial");
  // Whether the operator has hand-picked a plan; until then the plan tracks
  // the per-type default so switching demo↔trial updates the suggested plan.
  const [planTouched, setPlanTouched] = useState(false);
  const [plan, setPlan] = useState<CompPlanId>(COMP_DEFAULT_PLAN.trial);
  const [durationValue, setDurationValue] = useState("14");
  const [durationUnit, setDurationUnit] = useState<CompDurationUnit>("days");
  const [error, setError] = useState<string | null>(null);

  function onTypeChange(next: CompType) {
    setType(next);
    if (!planTouched) setPlan(COMP_DEFAULT_PLAN[next]);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const value = Number(durationValue);
    if (!Number.isFinite(value) || value <= 0) {
      setError("Enter a duration greater than zero.");
      return;
    }
    mutation.mutate(
      {
        workspaceId: tenant.id,
        type,
        plan,
        durationValue: value,
        durationUnit,
      },
      {
        onSuccess: () => {
          // Reset to defaults so the next open starts clean.
          setType("trial");
          setPlanTouched(false);
          setPlan(COMP_DEFAULT_PLAN.trial);
          setDurationValue("14");
          setDurationUnit("days");
          onClose();
        },
        onError: (err) => setError(err.message),
      },
    );
  }

  const tenantLabel = tenant.name?.trim() || tenant.id;

  return (
    <Modal
      open={open}
      title={`Grant demo/trial · ${tenantLabel}`}
      onClose={onClose}
      footer={
        <>
          <Btn variant="subtle" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Btn>
          <Btn
            type="submit"
            form="grant-comp-form"
            variant="primary"
            loading={mutation.isPending}
          >
            Grant access
          </Btn>
        </>
      }
    >
      <form id="grant-comp-form" className={styles.compForm} onSubmit={onSubmit}>
        <p className={styles.compFormHint}>
          Sets this workspace’s plan to the chosen tier for the duration, then
          locks the workspace when it expires (the owner can still subscribe to
          restore access). Demo/trial never unlocks enterprise SSO.
        </p>
        <div className={styles.compFormGrid}>
          <label className={styles.compField}>
            <span className={styles.compFieldLabel}>Type</span>
            <select
              className={styles.compSelect}
              value={type}
              onChange={(e) => onTypeChange(e.target.value as CompType)}
            >
              {COMP_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.compField}>
            <span className={styles.compFieldLabel}>Plan</span>
            <select
              className={styles.compSelect}
              value={plan}
              onChange={(e) => {
                setPlanTouched(true);
                setPlan(e.target.value as CompPlanId);
              }}
            >
              {COMP_PLAN_IDS.map((p) => (
                <option key={p} value={p}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.compField}>
            <span className={styles.compFieldLabel}>Duration</span>
            <input
              className={styles.compInput}
              type="number"
              min={1}
              step={1}
              value={durationValue}
              onChange={(e) => setDurationValue(e.target.value)}
              aria-label="Duration value"
            />
          </label>
          <label className={styles.compField}>
            <span className={styles.compFieldLabel}>Unit</span>
            <select
              className={styles.compSelect}
              value={durationUnit}
              onChange={(e) => setDurationUnit(e.target.value as CompDurationUnit)}
            >
              {COMP_DURATION_UNITS.map((u) => (
                <option key={u} value={u}>
                  {u.charAt(0).toUpperCase() + u.slice(1)}
                </option>
              ))}
            </select>
          </label>
        </div>
        {error && (
          <span className={styles.compError} role="alert">
            {error}
          </span>
        )}
      </form>
    </Modal>
  );
}

// ─── Billing ──────────────────────────────────────────────────────────

function BillingTab() {
  const billing = useAdminBilling();
  const flows = useAdminBillingFlows();
  const mrrRows = billing.data?.tenantsByMrr ?? [];
  useRegisterExport(
    mrrRows.length
      ? { filename: `admin-billing-${csvDateStamp()}.csv`, build: () => billingToCsv(mrrRows) }
      : null,
    [billing.data],
  );
  return (
    <div className={styles.tabBody}>
      <SectionHead
        title="Billing"
        desc="Monthly + annual recurring revenue, new and churned subscriptions this month, tenants ranked by revenue, upcoming renewals, and recent payments."
      />
      <BillingStats billing={billing} flows={flows} />
      <div className={styles.panel}>
        <div className={styles.panelHead}>Tenants by MRR</div>
        <BillingByMrr billing={billing} />
      </div>
      <div className={styles.twoCol}>
        <div className={styles.panel}>
          <div className={styles.panelHead}>
            Upcoming renewals · {flows.data?.renewalWindowDays ?? 14} days
          </div>
          <RenewalsPanel flows={flows} />
        </div>
        <div className={styles.panel}>
          <div className={styles.panelHead}>
            Recent payments · {flows.data?.paymentWindowDays ?? 30} days
          </div>
          <PaymentsPanel flows={flows} />
        </div>
      </div>
    </div>
  );
}

type FlowsQuery = ReturnType<typeof useAdminBillingFlows>;

/**
 * Value + hint for a "new subscriptions" / "churned" KPI tile, derived from the
 * flows query. These are GROSS counts this month (new = created, churned =
 * canceled) — NOT a true "net" that nets out upgrade/downgrade plan changes
 * (that needs subscription event history we don't store; tracked in FOLLOWUPS).
 * Degrades honestly when Stripe is unconfigured or the read failed.
 */
function flowCountTile(
  flows: FlowsQuery,
  pick: "newSubs" | "churned",
): { value: string; hint: string } {
  if (flows.isPending) return { value: "…", hint: "loading" };
  if (flows.isError) return { value: "—", hint: "couldn’t load" };
  const s = flows.data.subscriptions;
  if (!s.ok) {
    return {
      value: "—",
      hint: s.reason === "unconfigured" ? "Stripe not configured" : "Stripe read failed",
    };
  }
  const c = s[pick];
  if (c.count === 0) return { value: "0", hint: "none this month" };
  const sign = pick === "churned" ? "−" : "+";
  return { value: c.count.toLocaleString(), hint: `${sign}${fmtUsd(c.mrrUsd)}/mo` };
}

function BillingStats({
  billing,
  flows,
}: {
  billing: ReturnType<typeof useAdminBilling>;
  flows: FlowsQuery;
}) {
  // MRR / ARR come from the cheap scan-only query and render independently of
  // the (slower, failable) Stripe flows query — a Stripe outage never blanks
  // the instant MRR tile.
  const data: AdminBilling | undefined = billing.data;
  let mrr = "…";
  let arr = "…";
  let mrrHint = "loading";
  if (billing.isError) {
    mrr = "—";
    arr = "—";
    mrrHint = "couldn’t load";
  } else if (data) {
    mrr = fmtUsd(data.mrrUsd);
    arr = fmtUsd(data.arrUsd);
    mrrHint =
      data.activeSubscriptions === 1
        ? "1 active subscription"
        : `${data.activeSubscriptions.toLocaleString()} active subscriptions`;
  }
  const nu = flowCountTile(flows, "newSubs");
  const ch = flowCountTile(flows, "churned");
  return (
    <div className={styles.statRow}>
      <CostStat value={mrr} label="MRR" hint={mrrHint} />
      <CostStat value={arr} label="ARR (projected)" hint="annualized · MRR × 12" />
      <CostStat value={nu.value} label="New subscriptions this month" hint={nu.hint} />
      <CostStat value={ch.value} label="Churned this month" hint={ch.hint} />
    </div>
  );
}

function BillingByMrr({ billing }: { billing: ReturnType<typeof useAdminBilling> }) {
  if (billing.isPending) return <LoadingPane label="Loading tenants…" />;
  if (billing.isError) {
    return <ErrorPane message={billing.error.message} onRetry={() => void billing.refetch()} />;
  }
  const rows = billing.data.tenantsByMrr;
  if (rows.length === 0) {
    return (
      <EmptyPane
        icon="zap"
        title="No paying subscriptions yet."
        hint="Tenants appear here ranked by monthly recurring revenue once they have an active paid subscription."
      />
    );
  }
  const max = Math.max(...rows.map((r) => r.mrrUsd));
  return (
    <div className={styles.panelBody}>
      <ul className={styles.spendList}>
        {rows.map((r) => (
          <BillingMrrRow key={r.workspaceId} row={r} max={max} />
        ))}
      </ul>
    </div>
  );
}

function BillingMrrRow({ row, max }: { row: BillingTenant; max: number }) {
  return (
    <li className={styles.spendRow}>
      <span className={styles.spendName} title={row.name}>
        {row.name} <span className={styles.modelMeta}>· {row.plan} · {row.cadence}</span>
      </span>
      <span className={styles.spendAmount}>{fmtUsd(row.mrrUsd)}/mo</span>
      <span className={styles.spendTrack}>
        <span
          className={styles.spendFill}
          style={{ width: `${Math.max(2, (row.mrrUsd / max) * 100)}%` }}
        />
      </span>
    </li>
  );
}

/** Honest empty state for a flows section that's Stripe-unconfigured or errored. */
function FlowUnavailable({
  reason,
  message,
  what,
}: {
  reason: "unconfigured" | "error";
  message?: string | undefined;
  what: string;
}) {
  if (reason === "unconfigured") {
    return (
      <EmptyPane
        icon="info"
        title="Stripe isn’t configured for this deployment."
        hint={`Connect Stripe to see ${what}.`}
      />
    );
  }
  return <ErrorPane message={message ?? `Couldn’t load ${what} from Stripe.`} />;
}

function RenewalsPanel({ flows }: { flows: FlowsQuery }) {
  if (flows.isPending) return <LoadingPane label="Loading renewals…" />;
  if (flows.isError) {
    return <ErrorPane message={flows.error.message} onRetry={() => void flows.refetch()} />;
  }
  const s = flows.data.subscriptions;
  if (!s.ok) {
    return <FlowUnavailable reason={s.reason} message={s.message} what="upcoming renewals" />;
  }
  if (s.renewals.length === 0) {
    return (
      <EmptyPane
        icon="calendar"
        title="No renewals in this window."
        hint="Active subscriptions renewing in the next 14 days list here with tenant, plan, amount, and date."
      />
    );
  }
  return (
    <div className={styles.panelBody}>
      <ul className={styles.flowList}>
        {s.renewals.map((r, i) => (
          <RenewalRow key={`${r.workspaceId ?? "unknown"}#${i}`} row={r} />
        ))}
      </ul>
    </div>
  );
}

function RenewalRow({ row }: { row: BillingRenewal }) {
  const label = row.name ?? row.workspaceId ?? "Unknown tenant";
  return (
    <li className={styles.flowRow}>
      <span className={styles.flowName} title={label}>
        {label} <span className={styles.modelMeta}>· {row.plan} · {row.cadence}</span>
      </span>
      <span className={styles.flowAmount}>{fmtUsd(row.amountUsd)}</span>
      <span className={styles.flowWhen}>{fmtDate(row.renewsAt)}</span>
    </li>
  );
}

/** Charge status → audit-tag tone (reused pill styling). */
const PAYMENT_TONE: Record<string, AuditTone> = {
  succeeded: "positive",
  pending: "neutral",
  failed: "warn",
};

function PaymentsPanel({ flows }: { flows: FlowsQuery }) {
  if (flows.isPending) return <LoadingPane label="Loading payments…" />;
  if (flows.isError) {
    return <ErrorPane message={flows.error.message} onRetry={() => void flows.refetch()} />;
  }
  const p = flows.data.payments;
  if (!p.ok) {
    return <FlowUnavailable reason={p.reason} message={p.message} what="recent payments" />;
  }
  if (p.items.length === 0) {
    return (
      <EmptyPane
        icon="check-circle"
        title="No payments in this window."
        hint="Card charges from the last 30 days list here with tenant, amount, and status."
      />
    );
  }
  return (
    <div className={styles.panelBody}>
      <ul className={styles.flowList}>
        {p.items.map((row, i) => (
          <PaymentRow key={`${row.workspaceId ?? "unknown"}#${i}`} row={row} />
        ))}
      </ul>
    </div>
  );
}

function PaymentRow({ row }: { row: BillingPaymentRow }) {
  const label = row.name ?? row.workspaceId ?? "Unknown tenant";
  const tone = PAYMENT_TONE[row.status] ?? "neutral";
  return (
    <li className={styles.flowRow}>
      <span className={styles.flowName} title={label}>
        {label}{" "}
        <span className={`${styles.auditTag} ${AUDIT_TONE_CLASS[tone]}`}>{row.status}</span>
      </span>
      <span className={styles.flowAmount}>{fmtUsd(row.amountUsd)}</span>
      <span className={styles.flowWhen}>{fmtDate(row.at)}</span>
    </li>
  );
}

// ─── Cost ─────────────────────────────────────────────────────────────

const usdFmt = new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" });
const tokFmt = new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 });

function fmtUsd(n: number): string {
  if (n > 0 && n < 0.01) return "<$0.01";
  return usdFmt.format(n);
}
function fmtPct(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}
function fmtTokens(n: number): string {
  return tokFmt.format(Math.round(n));
}
/** "YYYY-MM-DDTHH" (UTC) → a short local hour label like "3 PM". */
function labelHour(hour: string): string {
  const d = new Date(`${hour}:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return hour;
  return d.toLocaleTimeString(undefined, { hour: "numeric" });
}

function CostTab() {
  const cost = useAdminCost();
  const spendRows = cost.data?.spendByTenant ?? [];
  useRegisterExport(
    spendRows.length
      ? { filename: `admin-cost-${csvDateStamp()}.csv`, build: () => costToCsv(spendRows) }
      : null,
    [cost.data],
  );
  return (
    <div className={styles.tabBody}>
      <SectionHead
        title="AI & infrastructure cost"
        desc="Estimated token spend across Anthropic direct + AWS Bedrock, prompt-cache hit rate, average tokens per draft, and a platform-wide AWS infrastructure estimate. Dollar figures are list-price estimates and count forward from when metering shipped."
      />
      <CostBody cost={cost} />
    </div>
  );
}

function CostBody({ cost }: { cost: ReturnType<typeof useAdminCost> }) {
  if (cost.isPending) return <LoadingPane label="Loading cost data…" />;
  if (cost.isError) {
    return <ErrorPane message={cost.error.message} onRetry={() => void cost.refetch()} />;
  }
  const c = cost.data;
  return (
    <>
      <CostStats c={c} />
      <div className={styles.panel}>
        <div className={styles.panelHead}>Spend by tenant · {c.windowDays} days</div>
        <SpendByTenant rows={c.spendByTenant} />
      </div>
      <div className={styles.twoCol}>
        <div className={styles.panel}>
          <div className={styles.panelHead}>Provider mix · {c.windowDays} days</div>
          <ProviderMix mix={c.providerMix} windowDays={c.windowDays} />
        </div>
        <div className={styles.panel}>
          <div className={styles.panelHead}>Cache health · last 24h</div>
          <CacheHealth points={c.cacheHealth} />
        </div>
      </div>
    </>
  );
}

function CostStats({ c }: { c: AdminCost }) {
  return (
    <div className={styles.statRow}>
      <CostStat value={fmtUsd(c.spend7dUsd)} label="Spend (7d)" hint="Anthropic + Bedrock · est." />
      <CostStat
        value={c.cacheHitRate == null ? "—" : fmtPct(c.cacheHitRate)}
        label="Cache hit rate"
        hint="target 85% · 30d"
      />
      <CostStat
        value={c.avgTokensPerDraft == null ? "—" : fmtTokens(c.avgTokensPerDraft)}
        label="Avg tokens / draft"
        hint="30d · draft generation"
      />
      <CostStat
        value={c.infraCost30dUsd == null ? "n/a" : fmtUsd(c.infraCost30dUsd)}
        label="Infra cost (30d)"
        hint={c.infraCost30dUsd == null ? "AWS estimate · unavailable" : "AWS estimate · all stacks"}
      />
    </div>
  );
}

function CostStat({ value, label, hint }: { value: string; label: string; hint: string }) {
  const muted = value === "—" || value === "n/a";
  return (
    <div className={styles.stat}>
      <span className={`${styles.statValue} ${muted ? "" : styles.statValueLive}`}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
      <span className={styles.statHint}>{hint}</span>
    </div>
  );
}

function SpendByTenant({ rows }: { rows: CostByTenant[] }) {
  const withSpend = rows.filter((r) => r.spendUsd > 0);
  if (withSpend.length === 0) {
    return (
      <EmptyPane
        icon="zap"
        title="No spend recorded yet."
        hint="Per-tenant spend appears here once drafts or trend scans run (metering counts forward from its deploy)."
      />
    );
  }
  const max = Math.max(...withSpend.map((r) => r.spendUsd));
  return (
    <div className={styles.panelBody}>
      <ul className={styles.spendList}>
        {withSpend.map((r) => (
          <li key={r.workspaceId} className={styles.spendRow}>
            <span className={styles.spendName} title={r.name}>{r.name}</span>
            <span className={styles.spendAmount}>{fmtUsd(r.spendUsd)}</span>
            <span className={styles.spendTrack}>
              <span
                className={styles.spendFill}
                style={{ width: `${Math.max(2, (r.spendUsd / max) * 100)}%` }}
              />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProviderMix({
  mix,
  windowDays,
}: {
  mix: AdminCost["providerMix"];
  windowDays: number;
}) {
  if (mix.anthropicUsd <= 0 && mix.bedrockUsd <= 0 && mix.byModel.length === 0) {
    return (
      <EmptyPane
        icon="info"
        title="No provider activity yet."
        hint="Anthropic-direct vs. AWS Bedrock share and a per-model breakdown appear here once generations run."
      />
    );
  }
  return (
    <div className={styles.panelBody}>
      <div className={styles.mixSplit}>
        <div className={styles.mixCard}>
          <span className={styles.mixValue}>{fmtUsd(mix.anthropicUsd)}</span>
          <span className={styles.mixLabel}>Anthropic direct</span>
        </div>
        <div className={styles.mixCard}>
          <span className={styles.mixValue}>{fmtUsd(mix.bedrockUsd)}</span>
          <span className={styles.mixLabel}>AWS Bedrock</span>
        </div>
      </div>
      {mix.byModel.length > 0 ? (
        <ul className={styles.modelList}>
          {mix.byModel.map((m) => (
            <li key={`${m.provider}#${m.modelId}`} className={styles.modelRow}>
              <span>
                {m.label} <span className={styles.modelMeta}>· {m.provider}</span>
              </span>
              <span className={styles.spendAmount}>{fmtUsd(m.spendUsd)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.muted}>No model usage in the last {windowDays} days.</p>
      )}
    </div>
  );
}

function CacheHealth({ points }: { points: CacheHealthPoint[] }) {
  const hasData = points.some((p) => p.hitRate != null);
  if (!hasData) {
    return (
      <EmptyPane
        icon="arrow-up-right"
        title="No cache activity in the last 24h."
        hint="Hourly prompt-cache hit-rate bars appear here once generations run."
      />
    );
  }
  return (
    <div className={styles.panelBody}>
      <div className={styles.series}>
        {points.map((p) => (
          <div
            key={p.hour}
            className={styles.seriesBar}
            title={`${labelHour(p.hour)} · ${
              p.hitRate == null ? "no activity" : `${Math.round(p.hitRate * 100)}% cached`
            }`}
          >
            {p.hitRate == null ? (
              <span className={styles.seriesEmpty} />
            ) : (
              <span
                className={styles.seriesFill}
                style={{ height: `${Math.max(4, p.hitRate * 100)}%` }}
              />
            )}
          </div>
        ))}
      </div>
      <div className={styles.seriesAxis}>
        <span>24h ago</span>
        <span>now</span>
      </div>
    </div>
  );
}

// ─── Ops ──────────────────────────────────────────────────────────────

function OpsTab() {
  return (
    <div className={styles.tabBody}>
      <SectionHead
        title="Ops alerts"
        desc="Things that need an operator: Lambda error rate, CloudFormation stack health, publish-worker queue depth, last successful sync per source kind, and degraded-publish tenants."
      />
      <div className={styles.opsActions}>
        {/* SCAFFOLD: re-fetch GET /api/admin/ops. Unwired. */}
        <Btn variant="secondary" size="sm" disabled title="Not wired up yet" iconLeft={<Icon name="refresh" size={13} />}>
          Refresh
        </Btn>
        {/* SCAFFOLD: POST /api/admin/ops/forward → Slack #ops. Unwired. */}
        <Btn variant="secondary" size="sm" disabled title="Not wired up yet" iconLeft={<Icon name="slack" size={13} />}>
          Forward to #ops
        </Btn>
      </div>
      {/* SCAFFOLD: GET /api/admin/ops → severity-ranked alert feed. */}
      <div className={styles.panel}>
        <div className={styles.panelHead}>Active alerts</div>
        <EmptyPane
          icon="alert"
          title="No alerts loaded."
          hint="Severity-colored alert rows (danger / warning / info) with tenant, detail, and a suggested action will render here."
        />
      </div>
    </div>
  );
}

// ─── Support ──────────────────────────────────────────────────────────

function SupportTab() {
  return (
    <div className={styles.tabBody}>
      <SectionHead
        title="Support inbox"
        desc="Customer-reported issues, prioritized, each linking through to the tenant it came from."
      />
      {/* SCAFFOLD: GET /api/admin/support → ticket list (status + priority filters). */}
      <div className={styles.panel}>
        <div className={styles.panelHead}>Open tickets</div>
        <EmptyPane
          icon="bell"
          title="No tickets loaded."
          hint="Incoming help requests will list here with tenant, requester, priority, status, and an open-thread action."
        />
      </div>
    </div>
  );
}

// ─── Audit ────────────────────────────────────────────────────────────

/** Short label + tone for each audited operator action. */
type AuditTone = "neutral" | "positive" | "warn";

const AUDIT_ACTION_META: Record<
  AdminAuditAction,
  { label: string; tone: AuditTone }
> = {
  set_plan: { label: "Plan override", tone: "neutral" },
  grant_comp: { label: "Comp granted", tone: "positive" },
  revoke_comp: { label: "Comp revoked", tone: "warn" },
  approve_waitlist: { label: "Waitlist approved", tone: "positive" },
  reject_waitlist: { label: "Waitlist rejected", tone: "warn" },
};

/** Tone → CSS-module class (inferred so the optional class type is preserved). */
const AUDIT_TONE_CLASS: Record<AuditTone, string | undefined> = {
  neutral: styles.auditTagNeutral,
  positive: styles.auditTagPositive,
  warn: styles.auditTagWarn,
};

/** Date + time for an audit row; falls back to the raw value. */
function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso || "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function AuditTab() {
  const audit = useAdminAudit();
  const events = audit.data?.events ?? [];
  useRegisterExport(
    events.length
      ? { filename: `admin-audit-${csvDateStamp()}.csv`, build: () => auditToCsv(events) }
      : null,
    [audit.data],
  );
  const windowDays = audit.data?.windowDays ?? 7;
  return (
    <div className={styles.tabBody}>
      <SectionHead
        title="Audit log"
        desc="Every privileged operator action — plan overrides, demo/trial grants and revokes, and waitlist approvals — appended as it happens and timestamped, newest first."
      />
      <div className={styles.panel}>
        <div className={styles.panelHead}>
          <span>Event log · last {windowDays} days</span>
          <Btn
            variant="subtle"
            size="sm"
            onClick={() => void audit.refetch()}
            disabled={audit.isFetching}
            iconLeft={<Icon name="refresh" size={13} />}
          >
            Refresh
          </Btn>
        </div>
        <AuditBody audit={audit} />
      </div>
    </div>
  );
}

function AuditBody({ audit }: { audit: ReturnType<typeof useAdminAudit> }) {
  if (audit.isPending) return <LoadingPane label="Loading audit events…" />;
  if (audit.isError) {
    return <ErrorPane message={audit.error.message} onRetry={() => void audit.refetch()} />;
  }
  const events = audit.data.events;
  if (events.length === 0) {
    return (
      <EmptyPane
        icon="info"
        title="No operator actions in the last 7 days."
        hint="Plan overrides, demo/trial grants and revokes, and waitlist approvals appear here as operators perform them."
      />
    );
  }
  return (
    <ul className={styles.auditList}>
      {events.map((e, i) => (
        <AuditRow key={`${e.createdAt}#${e.actorSub}#${i}`} event={e} />
      ))}
    </ul>
  );
}

function AuditRow({ event }: { event: AdminAuditEvent }) {
  const meta =
    AUDIT_ACTION_META[event.action] ?? {
      label: event.action,
      tone: "neutral" as AuditTone,
    };
  const target = event.targetWorkspaceId
    ? `workspace ${event.targetWorkspaceId}`
    : event.targetUsername
      ? `user ${event.targetUsername}`
      : null;
  const actor = event.actorEmail || event.actorSub || "unknown operator";
  return (
    <li className={styles.auditRow}>
      <span className={`${styles.auditTag} ${AUDIT_TONE_CLASS[meta.tone]}`}>
        {meta.label}
      </span>
      <div className={styles.auditMain}>
        <span className={styles.auditSummary}>{event.summary}</span>
        <span className={styles.auditMeta}>
          {actor}
          {target ? ` · ${target}` : ""}
        </span>
      </div>
      <span className={styles.auditWhen}>{fmtDateTime(event.createdAt)}</span>
    </li>
  );
}
